/**
 * Small stale-while-revalidate cache + poll layer for read-only data -
 * gateway REST reads and on-chain `readContract` calls. Deliberately never
 * touches writes (`POST`/`writeContract`); those go straight through
 * `gateway-api.js`/`dusk.writeContract` as before, unaffected by this
 * module.
 *
 * Three pieces:
 * - `readCached(key, fetcher, {ttlMs})` - one-shot read. Returns the
 *   cached value immediately if it's still within `ttlMs`; otherwise
 *   awaits a fresh fetch. Concurrent calls for the same key share one
 *   in-flight fetch instead of firing duplicate requests.
 * - `pollCached(key, fetcher, {intervalMs, onUpdate})` - subscribes to a
 *   key, calling `onUpdate(value, error)` immediately (serving cache if
 *   present) and again every `intervalMs` while the tab is visible.
 *   Polling pauses on `document.hidden` and catches up immediately on
 *   refocus rather than waiting for the next tick. Returns an
 *   unsubscribe function - callers MUST call it when done (e.g. on route
 *   change), or the interval leaks.
 * - `invalidate(key)` / `invalidatePrefix(prefix)` - marks cached value(s)
 *   stale so the next `readCached` or poll tick re-fetches instead of
 *   serving the old value. Call this right after your own transaction
 *   confirms (see `trackTx` call sites in trade-ticket.js etc.) so you see
 *   your own effect immediately instead of waiting out the poll interval.
 */

const entries = new Map();

function getEntry(key) {
  let e = entries.get(key);
  if (!e) {
    e = { value: undefined, error: null, fetchedAt: 0, promise: null, listeners: new Set() };
    entries.set(key, e);
  }
  return e;
}

function notify(key) {
  const e = entries.get(key);
  if (!e) return;
  for (const cb of e.listeners) cb(e.value, e.error);
}

function doFetch(key, fetcher) {
  const e = getEntry(key);
  if (e.promise) return e.promise;
  const p = (async () => {
    try {
      const value = await fetcher();
      e.value = value;
      e.error = null;
      e.fetchedAt = Date.now();
      return value;
    } catch (err) {
      e.error = err;
      throw err;
    } finally {
      e.promise = null;
      notify(key);
    }
  })();
  e.promise = p;
  return p;
}

/** @template T @param {string} key @param {() => Promise<T>} fetcher @returns {Promise<T>} */
export async function readCached(key, fetcher, { ttlMs = 5000 } = {}) {
  const e = getEntry(key);
  if (e.fetchedAt === 0) return doFetch(key, fetcher);
  const isStale = Date.now() - e.fetchedAt > ttlMs;
  if (isStale) doFetch(key, fetcher).catch(() => {});
  return e.value;
}

/** Marks `key` stale so the next read/poll tick re-fetches instead of serving cache. */
export function invalidate(key) {
  const e = entries.get(key);
  if (e) e.fetchedAt = 0;
}

/** Marks every cached key starting with `prefix` stale. */
export function invalidatePrefix(prefix) {
  for (const key of entries.keys()) {
    if (key.startsWith(prefix)) invalidate(key);
  }
}

/**
 * @template T
 * @param {string} key
 * @param {() => Promise<T>} fetcher
 * @param {{ intervalMs?: number, onUpdate?: (value: T|undefined, error: Error|null) => void, fresh?: boolean }} [opts]
 * @returns {() => void} unsubscribe - call this when the consumer unmounts
 */
export function pollCached(key, fetcher, { intervalMs = 15000, onUpdate, fresh = false } = {}) {
  const e = getEntry(key);
  const cb = (value, error) => onUpdate?.(value, error);
  e.listeners.add(cb);

  let timer = null;
  function tick() {
    if (document.hidden) return;
    doFetch(key, fetcher).catch(() => {});
  }
  function onVisibility() {
    if (!document.hidden) tick();
  }
  document.addEventListener("visibilitychange", onVisibility);
  timer = setInterval(tick, intervalMs);

  // Immediate first delivery. `fresh: true` always re-runs the fetcher -
  // required when the consumer keeps state in a page closure (browse
  // `markets = []`) rather than reading `pollCached`'s cached return value;
  // a warm cache hit would otherwise skip the fetcher and leave an empty UI.
  const first = fresh
    ? doFetch(key, fetcher)
    : readCached(key, fetcher, { ttlMs: intervalMs });
  first.then((v) => cb(v, null)).catch((err) => cb(undefined, err));

  return function unsubscribe() {
    e.listeners.delete(cb);
    if (timer) clearInterval(timer);
    timer = null;
    document.removeEventListener("visibilitychange", onVisibility);
  };
}
